/**
 * domains/market/engines/setup2/detection.ts — MER Algo Footprints v7.1H
 *
 * Deterministic setup detection with disciplined candidate suppression.
 *
 * Design principles:
 *   - Each setup family has explicit minimum conditions to prevent eager classification
 *   - After detection, a suppression pass removes weaker overlapping candidates
 *   - Suppression reasons are captured for audit/explainability
 *   - NO_VALID_SETUP is explicitly possible when no candidate survives
 */

import { SetupClass, SetupDirection, SetupEngineInput } from './types';
import type { TrapSeverity } from '../../../../shared/types';

// ─── Candidate (pre-score) ────────────────────────────────────────────────────

export interface SetupCandidate {
  setup_class:       SetupClass;
  setup_name:        string;
  direction:         SetupDirection;
  detected_signals:  string[];
  suppressed_by?:    string;   // set by suppression pass if candidate was removed

  // Derived context used by scorers
  hasTrigger:        boolean;
  triggerFreshness:  number;    // 0–1
  triggerSpecific:   boolean;
  hasConfirmation:   boolean;
  atExecutionZone:   boolean;
  stopLevelClear:    boolean;
  stopLevel:         number | null;
  entryPriceIdeal:   number | null;
  target1:           number | null;
  target2:           number | null;
  entryTriggerText:  string;
  confirmationText:  string;
  invalidationText:  string;
  targetBasisText:   string;
  timingNote:        string | null;

  // Minimum condition strength (0–3) — used in suppression pass
  evidenceStrength:  number;
}

// ─── Suppression reasons (for audit trace) ────────────────────────────────────

export interface SuppressedCandidate {
  setup_class:    SetupClass;
  setup_name:     string;
  reason:         string;
}

export interface DetectionResult {
  active:      SetupCandidate[];
  suppressed:  SuppressedCandidate[];
  no_signal:   boolean;  // true when nothing fired at all
}

const SEVERITY_RANK: Record<TrapSeverity, number> = {
  None: 0, Low: 1, Medium: 2, High: 3,
};

// ─── Core detector ────────────────────────────────────────────────────────────

function detectCandidates(inp: SetupEngineInput): SetupCandidate[] {
  const candidates: SetupCandidate[] = [];
  const { smc, regime, traps, zones, trend, candles, curPrice } = inp;
  if (!candles.length || !regime) return candidates;

  const candles_len = candles.length;
  const p           = curPrice;
  const vwap        = regime.curVWAP;
  const aboveVWAP   = p > vwap;
  const rvol        = regime.rvol;

  const bosRecent   = smc?.lastBOS   ? candles_len - 1 - smc.lastBOS.candleIdx   <= 15 : false;
  const chochRecent = smc?.lastCHOCH ? candles_len - 1 - smc.lastCHOCH.candleIdx <= 15 : false;
  const hasSweep    = !!(smc?.recentSweep?.reclaimed);
  const sweepRecent = hasSweep
    ? candles_len - 1 - (smc!.recentSweep!.candleIdx ?? candles_len) <= 10
    : false;

  const dispDir   = smc?.displacementDirection ?? 'none';
  const dispStr   = smc?.displacementStrength  ?? 0;
  const dispFresh = smc?.displacementFreshness ?? 0;
  const pd        = smc?.premiumDiscount;
  const pdZone    = pd?.zone ?? 'equilibrium';

  const maxTrap  = traps.reduce<TrapSeverity>((w, t) =>
    SEVERITY_RANK[t.severity] > SEVERITY_RANK[w] ? t.severity : w, 'None');

  const tDir      = trend?.trendDirection ?? 'neutral';
  const tState    = trend?.trendState     ?? 'transition';
  const tStrength = trend?.trendStrength  ?? 0;
  const inTrend   = tState === 'trend' || tState === 'pullback';
  const strongBull = tDir === 'bullish' && tStrength >= 0.4 && inTrend;
  const strongBear = tDir === 'bearish' && tStrength >= 0.4 && inTrend;

  const nextRes = zones.filter(z => z.type === 'resistance' && z.price > p)
                       .sort((a, b) => a.price - b.price)[0] ?? null;
  const nextSup = zones.filter(z => z.type === 'support'    && z.price < p)
                       .sort((a, b) => b.price - a.price)[0] ?? null;
  const nearKeyZone = zones.some(z => z.sig === 'High' && Math.abs(z.price - p) / p < 0.003);

  // ── 1. CONTINUATION — requires BOS + displacement in same direction ─────────
  // Minimum: recent BOS + displacement not stale
  if (bosRecent && smc?.lastBOS && dispFresh >= 0.25) {
    const bos    = smc.lastBOS;
    const bosDir: SetupDirection = bos.dir === 'bullish' ? 'LONG' : 'SHORT';
    const dispAligned = dispDir === (bosDir === 'LONG' ? 'bullish' : 'bearish');

    candidates.push({
      setup_class:    'CONTINUATION',
      setup_name:     `BOS ${bos.dir === 'bullish' ? 'Long' : 'Short'} Continuation`,
      direction:      bosDir,
      detected_signals: [
        `BOS ${bos.dir} @ ${bos.price?.toFixed(2) ?? 'N/A'} (${candles_len - 1 - bos.candleIdx}b ago)`,
        ...(dispAligned ? [`Displacement ${dispDir} str=${dispStr.toFixed(2)} fresh=${dispFresh.toFixed(2)}`] : []),
      ],
      hasTrigger:       true,
      triggerFreshness: Math.max(0, 1 - (candles_len - 1 - bos.candleIdx) / 18),
      triggerSpecific:  bos.price != null,
      hasConfirmation:  dispAligned && dispFresh >= 0.4,
      atExecutionZone:  nearKeyZone || dispFresh >= 0.6,
      stopLevelClear:   bos.price != null,
      stopLevel:        bos.price != null ? bos.price * (bosDir === 'LONG' ? 0.998 : 1.002) : null,
      entryPriceIdeal:  p,
      target1:          bosDir === 'LONG' ? nextRes?.price ?? null : nextSup?.price ?? null,
      target2:          null,
      entryTriggerText: `Pullback to BOS level ${bos.price?.toFixed(2) ?? ''}`,
      confirmationText: 'Displacement + RVOL expansion ≥ 1.2×',
      invalidationText: `Close below/above BOS level ${bos.price?.toFixed(2) ?? ''}`,
      targetBasisText:  bosDir === 'LONG' ? 'Next resistance zone' : 'Next support zone',
      timingNote:       dispFresh >= 0.7 ? 'Fresh — enter on next pullback' : 'Fading — wait for retest',
      evidenceStrength: (bos.price != null ? 1 : 0) + (dispAligned ? 1 : 0) + (dispFresh >= 0.5 ? 1 : 0),
    });
  }

  // ── 2. REVERSAL — requires CHOCH + is genuinely counter to dominant direction ─
  // More selective: suppress if continuation dominates in the same direction
  if (chochRecent && smc?.lastCHOCH) {
    const ch    = smc.lastCHOCH;
    const chDir: SetupDirection = ch.dir === 'bullish' ? 'LONG' : 'SHORT';
    const isTrueReversal = (chDir === 'LONG' && strongBear) ||
                           (chDir === 'SHORT' && strongBull) ||
                           (!strongBull && !strongBear);
    // Require: CHOCH must be fresher than BOS for same direction, or no BOS in that direction
    const bosInSameDir = smc?.lastBOS?.dir === ch.dir;
    const chochFresherThanBOS = bosInSameDir
      ? smc!.lastCHOCH!.candleIdx > (smc?.lastBOS?.candleIdx ?? -1)
      : true;

    if (isTrueReversal && chochFresherThanBOS) {
      candidates.push({
        setup_class:    'REVERSAL',
        setup_name:     `CHOCH ${ch.dir === 'bullish' ? 'Long' : 'Short'} Reversal`,
        direction:      chDir,
        detected_signals: [
          `CHOCH ${ch.dir} @ ${ch.price?.toFixed(2) ?? 'N/A'} (${candles_len - 1 - ch.candleIdx}b ago)`,
          ...(hasSweep ? ['Liquidity sweep reclaimed'] : []),
        ],
        hasTrigger:       true,
        triggerFreshness: Math.max(0, 1 - (candles_len - 1 - ch.candleIdx) / 12),
        triggerSpecific:  ch.price != null,
        hasConfirmation:  hasSweep && sweepRecent,
        atExecutionZone:  nearKeyZone,
        stopLevelClear:   ch.price != null,
        stopLevel:        ch.price != null ? ch.price * (chDir === 'LONG' ? 0.997 : 1.003) : null,
        entryPriceIdeal:  p,
        target1:          chDir === 'LONG' ? nextRes?.price ?? null : nextSup?.price ?? null,
        target2:          null,
        entryTriggerText: `CHOCH ${ch.dir} retest at ${ch.price?.toFixed(2) ?? ''}`,
        confirmationText: 'Reclaimed sweep + displacement in reversal direction + RVOL ≥ 1.2×',
        invalidationText: `Close ${chDir === 'LONG' ? 'below' : 'above'} CHOCH level ${ch.price?.toFixed(2) ?? ''}`,
        targetBasisText:  'Prior swing before CHOCH',
        timingNote:       sweepRecent ? 'Recent sweep reclaim — higher reversal probability' : null,
        evidenceStrength: (ch.price != null ? 1 : 0) + (hasSweep ? 1 : 0) + (sweepRecent ? 1 : 0),
      });
    }
  }

  // ── 3. TRAP REVERSAL — requires sweep + trap + reclaim confirmation ─────────
  // Minimum: BOTH a trap signal AND a reclaimed sweep (not just one)
  if (hasSweep && sweepRecent && traps.length > 0) {
    const topTrap = traps[0];
    // Only emit if trap is meaningful severity
    if (SEVERITY_RANK[topTrap.severity] >= 2) {  // Medium or High
      const trapDir: SetupDirection = topTrap.side === 'bull' ? 'SHORT' : 'LONG';
      candidates.push({
        setup_class:    'TRAP_REVERSAL',
        setup_name:     `${topTrap.type} Trap Reversal`,
        direction:      trapDir,
        detected_signals: [
          `Trap: ${topTrap.type} sev=${topTrap.severity}`,
          `Sweep reclaimed (${candles_len - 1 - (smc!.recentSweep!.candleIdx ?? candles_len)}b ago)`,
        ],
        hasTrigger:       true,
        triggerFreshness: sweepRecent ? 0.8 : 0.4,
        triggerSpecific:  false,
        hasConfirmation:  chochRecent,
        atExecutionZone:  nearKeyZone,
        stopLevelClear:   hasSweep,
        stopLevel:        smc?.recentSweep ? smc.recentSweep.level * (trapDir === 'LONG' ? 0.998 : 1.002) : null,
        entryPriceIdeal:  p,
        target1:          trapDir === 'LONG' ? nextRes?.price ?? null : nextSup?.price ?? null,
        target2:          null,
        entryTriggerText: 'Trap reclaim + CHOCH confirmation',
        confirmationText: 'Close beyond sweep level with expansion',
        invalidationText: `Re-test below swept level ${smc?.recentSweep?.level?.toFixed(2) ?? ''}`,
        targetBasisText:  'Previous swing before trap',
        timingNote:       SEVERITY_RANK[topTrap.severity] === 3 ? 'Extreme trap — reduce size' : null,
        evidenceStrength: (hasSweep ? 1 : 0) + (sweepRecent ? 1 : 0) + (SEVERITY_RANK[topTrap.severity] >= 2 ? 1 : 0),
      });
    }
  }

  // ── 4. LIQUIDITY SWEEP — developing only, trigger not yet confirmed ─────────
  // Only emit when sweep exists but CHOCH not confirmed yet (developing state)
  // Suppress if TRAP_REVERSAL or REVERSAL would cover it more precisely
  const trapReversalOrReversal = traps.length > 0 && hasSweep && sweepRecent && chochRecent;
  if (hasSweep && !sweepRecent && !chochRecent && !trapReversalOrReversal) {
    const sweepDir: SetupDirection = aboveVWAP ? 'LONG' : 'SHORT';
    candidates.push({
      setup_class:    'LIQUIDITY_SWEEP',
      setup_name:     `Liquidity Sweep ${sweepDir === 'LONG' ? 'Long' : 'Short'} (Developing)`,
      direction:      sweepDir,
      detected_signals: ['Liquidity sweep detected — awaiting reclaim + CHOCH'],
      hasTrigger:       false,
      triggerFreshness: 0.3,
      triggerSpecific:  false,
      hasConfirmation:  false,
      atExecutionZone:  nearKeyZone,
      stopLevelClear:   !!(smc?.recentSweep),
      stopLevel:        smc?.recentSweep?.level ?? null,
      entryPriceIdeal:  null,
      target1:          null,
      target2:          null,
      entryTriggerText: 'Await sweep level reclaim + CHOCH',
      confirmationText: 'Bullish/bearish engulf at swept level',
      invalidationText: `Extension beyond swept level ${smc?.recentSweep?.level?.toFixed(2) ?? ''}`,
      targetBasisText:  'Previous liquidity pool before sweep',
      timingNote:       'Developing — needs reclaim confirmation before entry',
      evidenceStrength: 1,  // always weak until reclaimed
    });
  }

  // ── 5. BREAKOUT CONFIRMATION — requires strong multi-signal alignment ───────
  // Stricter: BOS + strong disp (>=0.5) + fresh (>=0.65) + RVOL expansion (>=1.3)
  // Do NOT overlap with CONTINUATION unless disp is meaningfully stronger
  const continuationAlreadyFired = candidates.some(c => c.setup_class === 'CONTINUATION');
  const breakoutDir: SetupDirection = dispDir === 'bullish' ? 'LONG' : dispDir === 'bearish' ? 'SHORT' : 'NEUTRAL';
  if (bosRecent && dispStr >= 0.5 && dispFresh >= 0.65 && rvol >= 1.3 &&
      breakoutDir !== 'NEUTRAL' &&
      // Only emit breakout if disp strength is significantly stronger than continuation threshold
      (!continuationAlreadyFired || dispStr >= 0.65)) {
    candidates.push({
      setup_class:    'BREAKOUT_CONFIRMATION',
      setup_name:     `Breakout ${breakoutDir === 'LONG' ? 'Long' : 'Short'} Confirmation`,
      direction:      breakoutDir,
      detected_signals: [
        `Strong displacement ${dispDir} str=${dispStr.toFixed(2)} fresh=${dispFresh.toFixed(2)}`,
        `RVOL ${rvol.toFixed(2)}×`,
        `BOS ${smc?.lastBOS?.dir ?? ''}`,
      ],
      hasTrigger:       true,
      triggerFreshness: dispFresh,
      triggerSpecific:  true,
      hasConfirmation:  true,
      atExecutionZone:  nearKeyZone,
      stopLevelClear:   !!(smc?.lastBOS?.price),
      stopLevel:        smc?.lastBOS?.price ?? null,
      entryPriceIdeal:  p,
      target1:          breakoutDir === 'LONG' ? nextRes?.price ?? null : nextSup?.price ?? null,
      target2:          null,
      entryTriggerText: 'Confirmed breakout — pullback to BOS on limit',
      confirmationText: 'Already confirmed — monitor for continuation',
      invalidationText: `Close below/above BOS level ${smc?.lastBOS?.price?.toFixed(2) ?? ''}`,
      targetBasisText:  'Measured move from BOS to next key zone',
      timingNote:       'High momentum — limit order on pullback preferred',
      evidenceStrength: 3,  // all three conditions met
    });
  }

  // ── 6. MEAN REVERSION — selective: only in non-trending/ranging regimes ─────
  // Suppress in strong trend — mean reversion in a trend is dangerous
  const inStrongTrend = (strongBull || strongBear) && tStrength >= 0.55;
  const meanRevSupported = regime.dominant === 'meanReversion' || regime.dominant === 'noTrade';
  if (meanRevSupported && !inStrongTrend && (pdZone === 'premium' || pdZone === 'discount')) {
    const mrDir: SetupDirection = pdZone === 'discount' ? 'LONG' : 'SHORT';
    candidates.push({
      setup_class:    'MEAN_REVERSION',
      setup_name:     `Mean Reversion ${mrDir === 'LONG' ? 'Long' : 'Short'} at ${pdZone}`,
      direction:      mrDir,
      detected_signals: [
        `P/D zone: ${pdZone}`,
        `Regime: ${regime.dominant}`,
        ...(nearKeyZone ? ['At key liquidity zone'] : []),
      ],
      hasTrigger:       nearKeyZone,
      triggerFreshness: nearKeyZone ? 0.7 : 0.3,
      triggerSpecific:  nearKeyZone,
      hasConfirmation:  !!(smc?.lastCHOCH) || hasSweep,
      atExecutionZone:  nearKeyZone,
      stopLevelClear:   nearKeyZone,
      stopLevel:        mrDir === 'LONG' ? nextSup?.price ?? null : nextRes?.price ?? null,
      entryPriceIdeal:  p,
      target1:          pd ? pd.equilibrium : null,
      target2:          null,
      entryTriggerText: `Fade at ${pdZone} extreme toward equilibrium`,
      confirmationText: 'Rejection candle / inside bar at zone',
      invalidationText: `Extension beyond ${pdZone} extreme`,
      targetBasisText:  'Premium/Discount equilibrium',
      timingNote:       'Range bound — scale in / wider stops',
      evidenceStrength: (nearKeyZone ? 1 : 0) + (hasSweep ? 1 : 0) + (meanRevSupported ? 1 : 0),
    });
  }

  return candidates;
}

// ─── Suppression pass ─────────────────────────────────────────────────────────
// Rules (in priority order):
//  S1. BREAKOUT_CONFIRMATION dominates CONTINUATION in same direction (stronger signal)
//  S2. CONTINUATION dominates REVERSAL in SAME direction (can't be both at once)
//  S3. TRAP_REVERSAL with strong evidence dominates plain REVERSAL in same direction
//  S4. LIQUIDITY_SWEEP suppressed if TRAP_REVERSAL or REVERSAL covers same direction
//  S5. MEAN_REVERSION suppressed if any directional setup (CONT/BREAKOUT) is in same direction
//  S6. CONTINUATION suppressed if BREAKOUT_CONFIRMATION covers same direction (avoid double-count)

function suppressOverlapping(candidates: SetupCandidate[]): {
  active:     SetupCandidate[];
  suppressed: SuppressedCandidate[];
} {
  const suppressed: SuppressedCandidate[] = [];
  let active = [...candidates];

  const has = (cls: SetupClass, dir?: SetupDirection) =>
    active.some(c => c.setup_class === cls && (dir == null || c.direction === dir));

  const suppress = (cls: SetupClass, dir: SetupDirection | undefined, reason: string) => {
    const before = active.length;
    const removed = active.filter(c => c.setup_class === cls && (dir == null || c.direction === dir));
    active = active.filter(c => !(c.setup_class === cls && (dir == null || c.direction === dir)));
    removed.forEach(c => suppressed.push({ setup_class: c.setup_class, setup_name: c.setup_name, reason }));
  };

  // S1: BREAKOUT dominates CONTINUATION in same direction
  for (const dir of ['LONG', 'SHORT'] as SetupDirection[]) {
    if (has('BREAKOUT_CONFIRMATION', dir) && has('CONTINUATION', dir))
      suppress('CONTINUATION', dir, 'Suppressed by stronger BREAKOUT_CONFIRMATION in same direction');
  }

  // S2: CONTINUATION dominates REVERSAL in SAME direction (contradiction)
  for (const dir of ['LONG', 'SHORT'] as SetupDirection[]) {
    if (has('CONTINUATION', dir) && has('REVERSAL', dir))
      suppress('REVERSAL', dir, 'Suppressed — CONTINUATION in same direction takes priority');
  }

  // S3: TRAP_REVERSAL dominates plain REVERSAL in same direction (more specific signal)
  for (const dir of ['LONG', 'SHORT'] as SetupDirection[]) {
    const tr = active.find(c => c.setup_class === 'TRAP_REVERSAL' && c.direction === dir);
    if (tr && tr.evidenceStrength >= 2 && has('REVERSAL', dir))
      suppress('REVERSAL', dir, 'Suppressed by more specific TRAP_REVERSAL in same direction');
  }

  // S4: LIQUIDITY_SWEEP suppressed if TRAP_REVERSAL or REVERSAL covers same direction
  for (const dir of ['LONG', 'SHORT'] as SetupDirection[]) {
    if ((has('TRAP_REVERSAL', dir) || has('REVERSAL', dir)) && has('LIQUIDITY_SWEEP', dir))
      suppress('LIQUIDITY_SWEEP', dir, 'Suppressed — TRAP_REVERSAL/REVERSAL provides more specific sweep signal');
  }

  // S5: MEAN_REVERSION suppressed when strong directional continuation exists
  for (const dir of ['LONG', 'SHORT'] as SetupDirection[]) {
    if ((has('CONTINUATION', dir) || has('BREAKOUT_CONFIRMATION', dir)) && has('MEAN_REVERSION', dir))
      suppress('MEAN_REVERSION', dir, 'Suppressed — directional continuation dominates in this regime');
  }

  // Cap: max 3 active candidates (keep strongest by evidenceStrength desc)
  if (active.length > 3) {
    const sorted = [...active].sort((a, b) => b.evidenceStrength - a.evidenceStrength);
    const kept   = new Set(sorted.slice(0, 3).map((_, i) => i));
    sorted.slice(3).forEach(c =>
      suppressed.push({ setup_class: c.setup_class, setup_name: c.setup_name, reason: 'Capped at 3 candidates — weaker evidence' }));
    active = sorted.slice(0, 3);
  }

  return { active, suppressed };
}

// ─── Public entry ─────────────────────────────────────────────────────────────

export function detectSetupCandidates(inp: SetupEngineInput): SetupCandidate[] {
  const raw                       = detectCandidates(inp);
  const { active, suppressed }    = suppressOverlapping(raw);
  return active;
}

/** Full detection result including suppression trace — for audit. */
export function detectSetupCandidatesFull(inp: SetupEngineInput): DetectionResult {
  const raw                    = detectCandidates(inp);
  const { active, suppressed } = suppressOverlapping(raw);
  return {
    active,
    suppressed,
    no_signal: active.length === 0 && raw.length === 0,
  };
}
